import React, { useEffect, useState } from 'react'
import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import SimpleDialog from '../../../simpleDialog/simpleDialog'
import { CardActionProps } from '../../../cardActions/cardActions'
import { useTranslation } from 'react-i18next'
import { ProcedureDocument } from '../../../../../api/Argentia/endpoints/procedures'
import { AnnexedParameters, DocumentTypes } from '../../../../../api/Argentia/enums'
import { getCustomerDomain } from '../../../../../redux'
import { enumKeyToValue } from '../../../../../helpers'

interface ClaimInformationFormDialogProps {
  open: boolean
  onClose: (formData?: any) => void
  infoTypeList: SelectOption[]
  isGasNatural: boolean
  procedureDocument: ProcedureDocument[]
}

interface ClaimInformationFormModel {
  informationType?: string
  description?: string
  comments?: string
}

const initialFormData: ClaimInformationFormModel = {
  informationType: undefined,
  description: undefined,
  comments: undefined
}

export const ClaimInformationFormDialog: React.FC<ClaimInformationFormDialogProps> = ({
  open,
  onClose,
  infoTypeList,
  isGasNatural,
  procedureDocument
}) => {
  const [t] = useTranslation(['procedures', 'globalerisLibrary'])
  const [formData, setFormData] = useState<ClaimInformationFormModel>(initialFormData)
  const [formValid, setFormValid] = useState(false)
  const [showDescription, setShowDescription] = useState(false)
  const [readOnlyInfoType, setReadOnlyInfoType] = useState(false)
  const [title, setTitle] = useState<string>(
    'procedures:components.annexedParamCustomFormDialog.title.claimReply'
  )

  useEffect(() => {
    if (open) {
      setFormData(initialFormData)
      setShowDescription(false)
      setFormValid(false)
      setReadOnlyInfoType(false)
      setTitle('procedures:components.annexedParamCustomFormDialog.title.claimReply')
      buildFormFromProcedure()
    }
  }, [open])

  const buildFormFromProcedure = () => {
    if (isGasNatural) {
      const a25Doc = getDocumentType(procedureDocument, DocumentTypes.A25)
      if (!a25Doc?.length) return

      const doc = a25Doc[0]
      const interventionTypeParam = getAnnexedParam(doc, AnnexedParameters.TIPO_INTERVENCION)
      const infoTypeParam = getAnnexedParam(doc, AnnexedParameters.GAS_TIPO_INFORMACION_ADICIONAL)

      if (interventionTypeParam?.value === '02') {
        // Solicitud de más información
        setFormData(prev => ({
          ...prev,
          informationType: infoTypeParam?.value
        }))
        setReadOnlyInfoType(infoTypeParam?.value === '06')
        setShowDescription(infoTypeParam?.value === '06')
      } else {
        setTitle('procedures:components.annexedParamCustomFormDialog.title.retypingReply')
      }
    } else {
      const paso03Doc = getDocumentType(procedureDocument, DocumentTypes.Paso_03)
      if (!paso03Doc?.length) return

      const doc = paso03Doc[0]
      const communicationTypeParam = getAnnexedParam(doc, AnnexedParameters.PWR_TIPO_COMUNICACION)
      const infoTypeParam = getAnnexedParam(doc, AnnexedParameters.PWR_TIPO_INFORMACION_ADICIONAL)

      if (communicationTypeParam?.value === '01') {
        // Solicitud de información adicional
        setFormData(prev => ({
          ...prev,
          informationType: infoTypeParam?.value
        }))
        setReadOnlyInfoType(true)
        setShowDescription(infoTypeParam?.value === '06')
      } else {
        setTitle('procedures:components.annexedParamCustomFormDialog.title.retypingReply')
      }
    }
  }

  useEffect(() => {
    const valid = !!formData.informationType && (!showDescription || !!formData.description)
    setFormValid(valid)
  }, [formData, showDescription])

  const getDocumentType = (documentsList: ProcedureDocument[], docType: DocumentTypes) => {
    return (
      documentsList?.filter(
        (doc: ProcedureDocument) => enumKeyToValue(DocumentTypes, doc.type) === docType
      ) || []
    )
  }

  const getAnnexedParam = (doc: ProcedureDocument, paramKey: AnnexedParameters) => {
    return doc.parameters?.find(param => enumKeyToValue(AnnexedParameters, param.type) === paramKey)
  }

  const handleTypeChange = (value: any) => {
    setFormData(prev => ({
      ...prev,
      informationType: value,
      description: value === '06' ? prev.description : undefined
    }))
    setShowDescription(value === '06')
  }

  const sections: Section[] = [
    {
      inputs: [
        {
          name: 'informationType',
          label: t('components.annexedParamCustomFormDialog.inputs.informationType'),
          type: DataType.SELECT,
          selectOptions: infoTypeList,
          required: true,
          readOnly: readOnlyInfoType,
          defaultValue: formData.informationType,
          onChange: handleTypeChange
        },
        {
          name: 'description',
          label: t('components.annexedParamCustomFormDialog.inputs.description'),
          type: DataType.LONGTEXT,
          maxLength: 255,
          required: showDescription,
          display: showDescription,
          onChange: value => setFormData(prev => ({ ...prev, description: value.toString() }))
        },
        {
          name: 'comments',
          label: t('components.annexedParamCustomFormDialog.inputs.comments'),
          type: DataType.LONGTEXT,
          required: !isGasNatural,
          maxLength: 4000,
          onChange: value => setFormData(prev => ({ ...prev, comments: value.toString() }))
        }
      ]
    }
  ]

  const options: FormOptions = {
    elevation: 0,
    columns: 2,
    analytics: {
      customer: getCustomerDomain(),
      view: t('components.annexedParamCustomFormDialog.title.claimReply')
    }
  }

  const footerActions: CardActionProps[] = [
    {
      label: t('globalerisLibrary:form.actions.cancel'),
      color: 'secondary',
      handleActionClick: () => onClose(undefined)
    },
    {
      label: t('globalerisLibrary:form.actions.save'),
      color: 'primary',
      disabled: !formValid,
      handleActionClick: () => onClose(formData)
    }
  ]

  return (
    <SimpleDialog
      open={open}
      handleClose={() => onClose(undefined)}
      footerActions={footerActions}
      maxWidth="md"
      formStyle="formContent"
    >
      <Form sections={sections} data={formData} options={options} title={t(title)} />
    </SimpleDialog>
  )
}
